/**
 * SpeakerAttributionDialog — verifies every bullet's `speaker`
 * against the attendee list. Transcript-sourced meetings only.
 * Accepted fixes replace the speaker field; accepted removals clear
 * it entirely.
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';
import type { StructuredContent } from '@ojb/shared';

interface Issue {
  topicIndex: number;
  bulletIndex: number;
  currentSpeaker: string;
  proposedSpeaker: string | null;
  confidence: 'high' | 'medium' | 'low';
  rationale: string;
}

interface UsageMeta {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  durationMs: number;
}

interface Meeting {
  id: string;
  structuredContent: Record<string, unknown>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
  onApply: (next: StructuredContent) => void;
}

export function SpeakerAttributionDialog({
  isOpen,
  onClose,
  meeting,
  onApply,
}: Props) {
  const [accepted, setAccepted] = useState<Set<number>>(new Set());

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{
        issues: Issue[];
        attendeeNames: string[];
        usage: UsageMeta;
      }>(`/meetings/${meeting.id}/check-speakers`),
    onSuccess: (data) => {
      setAccepted(
        new Set(
          data.issues
            .map((it, i) => (it.confidence === 'high' ? i : -1))
            .filter((i) => i >= 0),
        ),
      );
    },
  });

  useEffect(() => {
    if (isOpen && !mutation.data && !mutation.isPending && !mutation.isError) {
      mutation.mutate();
    }
    if (!isOpen) {
      mutation.reset();
      setAccepted(new Set());
    }
  }, [isOpen]);

  const issues = mutation.data?.issues ?? [];
  const acceptedCount = accepted.size;

  const toggle = (i: number) =>
    setAccepted((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const apply = () => {
    if (acceptedCount === 0) {
      onClose();
      return;
    }
    const current = meeting.structuredContent as unknown as StructuredContent;
    const topics = (current.topics ?? []).map((t) => ({
      ...t,
      bullets: t.bullets.map((b) => ({ ...b })),
    }));
    for (const i of accepted) {
      const it = issues[i];
      if (!it) continue;
      const topic = topics[it.topicIndex];
      if (!topic) continue;
      const bullet = topic.bullets[it.bulletIndex];
      if (!bullet) continue;
      // null proposedSpeaker means "clear the field" — usually when
      // the speaker wasn't in the attendee list and couldn't be
      // resolved.
      bullet.speaker = it.proposedSpeaker ?? undefined;
    }
    onApply({ ...current, topics });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Check speaker attribution"
    >
      <ModalHeader
        title="Check speaker attribution"
        subtitle="For transcripts, Claude cross-references each bullet's speaker against the attendee list and proposes fixes for misspellings, placeholders, and stray speakers."
        onClose={onClose}
      />
      <ModalBody>
        {mutation.isPending && (
          <div className="py-10 text-center text-sm italic text-charcoal/60">
            Cross-referencing speakers with attendees…
          </div>
        )}
        {mutation.isError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'Speaker check failed'}
          </div>
        )}
        {mutation.data && issues.length === 0 && (
          <div className="rounded-2xl border border-pine/25 bg-pine/5 p-4 text-sm text-pine">
            ✓ Every bullet&rsquo;s speaker already matches someone on
            the attendee list. Nothing to fix.
          </div>
        )}
        {issues.length > 0 && (
          <ul className="space-y-3">
            {issues.map((it, i) => {
              const isAccepted = accepted.has(i);
              return (
                <li
                  key={`${it.topicIndex}-${it.bulletIndex}-${i}`}
                  className={
                    'rounded-2xl border p-3 transition-colors ' +
                    (isAccepted
                      ? 'border-pine/40 bg-pine/5'
                      : 'border-charcoal/15 bg-white')
                  }
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isAccepted}
                      onChange={() => toggle(i)}
                      className="mt-1 h-4 w-4 accent-pine"
                    />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <ConfidencePill level={it.confidence} />
                        <span className="text-[10px] text-charcoal/50">
                          Topic {it.topicIndex + 1} · Bullet {it.bulletIndex + 1}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-charcoal/70">Current:</span>{' '}
                        <span className="font-medium text-charcoal">
                          {it.currentSpeaker}
                        </span>
                        <span className="mx-2 text-charcoal/40">→</span>
                        <span className="text-pine/70">Proposed:</span>{' '}
                        {it.proposedSpeaker ? (
                          <span className="font-medium text-pine">
                            {it.proposedSpeaker}
                          </span>
                        ) : (
                          <span className="italic text-charcoal/55">
                            clear the field (speaker not in attendee list)
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-charcoal/60">
                        {it.rationale}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ModalBody>
      <ModalFooter className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/60">
          {acceptedCount > 0
            ? `${acceptedCount} selected`
            : issues.length > 0
              ? 'None selected'
              : ''}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={acceptedCount === 0}
            className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
          >
            Apply {acceptedCount > 0 ? `${acceptedCount}` : 'selected'}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

function ConfidencePill({ level }: { level: 'high' | 'medium' | 'low' }) {
  const map = {
    high: 'bg-pine/10 text-pine',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-charcoal/10 text-charcoal/70',
  };
  return (
    <span
      className={
        'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ' +
        map[level]
      }
    >
      {level} confidence
    </span>
  );
}
